import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ListBulletIcon,
  ChatBubbleBottomCenterIcon,
  ClockIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageLoading from "../../components/PageLoading";
import ProfileHeader from "./ProfileHeader";
import TabsNavigation from "./TabsNavigation";
import UserLists from "./UserLists";
import TasteProfileCard from "./TasteProfileCard";
import { UserContext } from "../../context/UserContext";
import {
  GetUserByName,
  GetUserStats,
  GetTasteProfile,
  GetAllUserLists,
} from "../../api-requests/Get";
import {
  FollowUser,
  UnfollowUser,
  GetFollowStatus,
} from "../../api-requests/Follow";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import { Helmet } from "react-helmet-async";

interface User {
  id: string;
  display_name: string;
  username: string;
  bg_color: string;
  createdAt: string;
  profileImg?: string;
}

interface UserList {
  id: string;
  title: string;
  createdAt: string;
  category: {
    id: string;
    name: string;
  };
  views: number;
  likes: number;
  isLiked?: boolean;
  items: Array<{
    id: string;
    name: string;
    rank: number;
  }>;
}

const Profile = () => {
  const { userid } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [profile, setProfile] = useState<User | null>(null);
  const [userLists, setUserLists] = useState<UserList[]>([]);
  const [activeTab, setActiveTab] = useState("lists");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    followersCount: 0,
    followingCount: 0,
    listsCount: 0,
    goatScore: 0,
  });
  const [tasteProfile, setTasteProfile] = useState({
    goldenEra: 0,
    modern: 0,
    underground: 0,
    mainstream: 0,
  });
  const [fullTasteProfile, setFullTasteProfile] = useState<any>(null);
  const [tasteProfileMessage, setTasteProfileMessage] = useState<string>("");
  const [topGenres, setTopGenres] = useState<string[]>([]);
  const [communityMatch, setCommunityMatch] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const tabs = [
    {
      id: "lists",
      label: "Lists",
      icon: ListBulletIcon,
      count: stats.listsCount,
    },
    {
      id: "debates",
      label: "Debates",
      icon: ChatBubbleBottomCenterIcon,
      count: 3,
    },
    //{ id: "activity", label: "Activity", icon: ClockIcon },
    //{ id: "likes", label: "Likes", icon: HeartIcon, count: 47 },
  ];

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userid) return;

      try {
        setLoading(true);
        const userProfile = await GetUserByName(userid);

        if (userProfile) {
          setProfile(userProfile);

          // Fetch user statistics and follow status in parallel
          try {
            const headers = getBearerHeaders();
            const [statsResponse, followStatusResponse] = await Promise.all([
              GetUserStats(userProfile.id, headers),
              GetFollowStatus(userProfile.id, headers).catch(() => null),
            ]);

            if (statsResponse.success) {
              setStats((prev) => ({
                ...prev,
                ...statsResponse.stats,
              }));
            }

            if (followStatusResponse?.success) {
              setIsFollowing(followStatusResponse.isFollowing ?? false);
            }
          } catch (error) {
            console.log("Stats not available:", error);
            // Keep default stats if API call fails
          }

          // Fetch taste profile
          try {
            const headers = getBearerHeaders();
            const tasteResponse = await GetTasteProfile(
              userProfile.id,
              headers,
            );
            if (tasteResponse.success) {
              setTasteProfile(tasteResponse.data.tasteProfile);
              setTopGenres(tasteResponse.data.topGenres);
              setCommunityMatch(tasteResponse.data.communityMatch);
              setFullTasteProfile(tasteResponse.data.fullProfile);
              if (tasteResponse.message) {
                setTasteProfileMessage(tasteResponse.message);
              }
            }
          } catch (error) {
            console.log("Taste profile not available:", error);
            // Keep default values if API call fails
          }

          // Fetch user's lists using the new API
          try {
            const userListsData = await GetAllUserLists(userProfile.id);

            // Transform the data to match our interface
            const transformedLists: UserList[] =
              userListsData?.map((list: any) => {
                return {
                  id: list.data.id,
                  title:
                    list.data.ListType?.name ||
                    list.data.title ||
                    `Top 10 ${list.data.Category?.name || "Items"}`,
                  createdAt: list.data.createdAt,
                  category: {
                    id: list.data.categoryId || "",
                    name: list.data.Category?.name || "General",
                  },
                  views: Math.floor(Math.random() * 1000) + 100, // Mock data
                  likes: Math.floor(Math.random() * 100) + 10, // Mock data
                  isLiked: false,
                  engagement: list.engagement,
                  items:
                    list.data.UserListItems?.slice(0, 10).map(
                      (item: any, index: number) => ({
                        id: item.Item?.id || "",
                        name: item.Item?.name || "Unknown Item",
                        rank: index + 1,
                      }),
                    ) || [],
                };
              }) || [];

            setUserLists(transformedLists);

            // Update listsCount with actual count
            setStats((prev) => ({
              ...prev,
              listsCount: transformedLists.length,
            }));
          } catch (error) {
            console.error("Error fetching user lists:", error);
            setUserLists([]); // Set empty array if lists can't be loaded
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userid]);

  const handleListClick = (listId: string) => {
    navigate(`/l/${listId}`);
  };

  const handleLikeToggle = (listId: string, isLiked: boolean) => {
    // TODO: Implement like functionality
    setUserLists((prev) =>
      prev.map((list) =>
        list.id === listId
          ? { ...list, isLiked, likes: list.likes + (isLiked ? 1 : -1) }
          : list,
      ),
    );
  };

  const handleFollow = async () => {
    if (!user?.isloggedin || !profile || followLoading) return;
    setFollowLoading(true);
    try {
      const headers = getBearerHeaders();
      if (isFollowing) {
        await UnfollowUser(profile.id, headers);
        setIsFollowing(false);
        setStats((prev) => ({
          ...prev,
          followersCount: Math.max(0, prev.followersCount - 1),
        }));
      } else {
        await FollowUser(profile.id, headers);
        setIsFollowing(true);
        setStats((prev) => ({
          ...prev,
          followersCount: prev.followersCount + 1,
        }));
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleCompareLists = () => {
    // TODO: Implement compare functionality
    console.log("Compare lists");
  };

  const handleShareProfile = () => {
    // TODO: Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: `${profile?.display_name}'s Profile - 10goats.com`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading || !profile) {
    return <PageLoading />;
  }

  const isOwnProfile = user?.isloggedin && user?.id === profile.id;

  const renderTabContent = () => {
    switch (activeTab) {
      case "lists":
        return (
          <UserLists
            lists={userLists}
            onListClick={handleListClick}
            onLikeToggle={handleLikeToggle}
            isOwnProfile={isOwnProfile}
          />
        );
      case "debates":
        return (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <ChatBubbleBottomCenterIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No debates yet
            </h3>
            <p className="text-gray-500">
              Debate participation will appear here.
            </p>
          </div>
        );
      case "activity":
        return (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <ClockIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No recent activity
            </h3>
            <p className="text-gray-500">Recent activity will appear here.</p>
          </div>
        );
      case "likes":
        return (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <HeartIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No likes yet
            </h3>
            <p className="text-gray-500">Liked lists will appear here.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>10 GOATs Profile of {profile.display_name}</title>
        <meta
          property="og:title"
          content={`10 GOATs Profile Of ${profile.display_name}`}
        />
        <meta
          property="og:description"
          content="Curate your ultimate top 10 emcee lists at 10goats.com!"
        />
        <meta
          property="og:image"
          content="https://10goats.com/ogimage.jpg?v=4"
        />
        <meta
          property="og:url"
          content={`https://10goats.com/u/${profile.display_name}`}
        />
        <meta
          name="description"
          content={`Here's ${profile.display_name}'s 10 GOATs, Curate your ultimate top 10 emcee lists at 10goats.com!`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="10goats.com" />
        <meta
          property="twitter:url"
          content={`https://10goats.com/u/${profile.display_name}`}
        />
        <meta
          name="twitter:title"
          content={`10 GOATs Profile Of ${profile.display_name}`}
        />
        <meta
          name="twitter:description"
          content={`Here's ${profile.display_name}'s 10 GOATs, Curate your ultimate top 10 emcee lists at 10goats.com!`}
        />
        <meta
          name="twitter:image"
          content="https://10goats.com/twittercard.jpg?v=4"
        />
      </Helmet>

      <Header />

      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Profile Header */}
        <ProfileHeader
          profile={profile}
          stats={stats}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          followLoading={followLoading}
          onFollow={handleFollow}
          onCompareLists={handleCompareLists}
          onShareProfile={handleShareProfile}
        />
        <div className="separator-fade" />
        {/* Tabs Navigation */}
        <TabsNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs}
        />

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">{renderTabContent()}</div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <TasteProfileCard
              tasteProfile={tasteProfile}
              communityMatch={communityMatch || 85} // Fallback to 85 if not loaded
              topGenres={topGenres}
              totalLists={stats.listsCount}
              fullProfile={fullTasteProfile}
              message={tasteProfileMessage}
            />

            {/* Additional sidebar components can be added here */}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Profile;
